// src/Pages/Estabilishment/MyEstablishments/index.tsx

import React, { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { getEstablishmentDashboard } from "../../../Services/EstablishmentDashboard";
import type { IRestDashboard } from "../../../Services/EstablishmentDashboard";
import {
  searchEstabilishments,
  addEstabilishmentToAccount,
  checkEstabilishmentsTaken,
  postUserEstabilishmentAttachment,
} from "../../../Services/Estabilishment";
import { getApiErrorMessage } from "../../../Services/apiError";
import {
  getMyEstabilishments,
  updateUserEstabilishmentProfile,
} from "../../../Services/Stripe";
import {
  IUserEstabilishment,
  IRestGetMyEstabilishments,
} from "../../../Services/Interfaces";
import { api } from "../../../Services/Api";
import { useT } from "../../../i18n";

const AuthImage: React.FC<{ photoId: string | null | undefined; style?: React.CSSProperties; fallback: React.ReactNode }> = ({ photoId, style, fallback }) => {
  const [src, setSrc] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!photoId) return;
    let objectUrl: string;
    api.get(`/pt_BR/midway_photo/${photoId}`, { responseType: "blob", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
      .then((res: any) => { objectUrl = URL.createObjectURL(res.data); setSrc(objectUrl); })
      .catch(() => setSrc(null));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [photoId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!src) return <>{fallback}</>;
  return <img src={src} alt="" style={style} />;
};

dayjs.locale("pt-br");

// ─── Icons ────────────────────────────────────────────────────────────────────

const ic = (p: React.ReactNode) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p}</svg>
);

const IconStore    = () => ic(<><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>);
const IconPin      = () => ic(<><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>);
const IconCalendar = () => ic(<><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>);
const IconArrow    = () => ic(<><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></>);
const IconCheck    = () => ic(<polyline points="20 6 9 17 4 12"/>);
const IconClock    = () => ic(<><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>);
const IconEdit     = () => ic(<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>);
const IconInsta    = () => ic(<><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><line x1="17.5" y1="6.5" x2="17.5" y2="6.5"/></>);
const IconGlobe    = () => ic(<><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>);
const IconEmpty    = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

// ─── Status helpers ───────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ approved: boolean }> = ({ approved }) => {
  const t = useT();

  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "4px 12px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      backgroundColor: approved ? "#e6f7ef" : "#fff7e6",
      color:           approved ? "#1a7a4a" : "#b45309",
      whiteSpace: "nowrap",
    }}>
      {approved ? <IconCheck /> : <IconClock />}
      {approved ? t("Aprovado") : t("Aguardando aprovação")}
    </span>
  );
};

// ─── Establishment card ───────────────────────────────────────────────────────

const EstablishmentCard: React.FC<{
  ue: IUserEstabilishment;
  onDetails: (ue: IUserEstabilishment) => void;
}> = ({ ue, onDetails }) => {
  const t = useT();
  const estab = ue.estabilishment;

  return (
    <div style={{
      background: "#fff",
      border: "1.5px solid #efefef",
      borderRadius: 14,
      display: "flex",
      alignItems: "center",
      gap: 20,
      padding: "20px 24px",
      transition: "box-shadow 0.15s",
    }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.07)")}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
    >
      {/* Logo / placeholder */}
      <div style={{
        width: 68, height: 68, borderRadius: 12, flexShrink: 0,
        overflow: "hidden", background: "#f5f5f5",
        display: "flex", alignItems: "center", justifyContent: "center",
        border: "1px solid #efefef",
      }}>
        <AuthImage
          photoId={estab?.image}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          fallback={<span style={{ color: "#ccc" }}><IconStore /></span>}
        />
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: "#111" }}>
            {estab?.name ?? "—"}
          </span>
          <StatusBadge approved={!!ue.isModered} />
        </div>

        {estab?.addressInGoogleFormat && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 5, marginBottom: 4 }}>
            <span style={{ color: "#aaa", flexShrink: 0, marginTop: 1 }}><IconPin /></span>
            <span style={{ fontSize: 13, color: "#666", lineHeight: 1.4 }}>
              {estab.addressInGoogleFormat}
            </span>
          </div>
        )}

        {ue.createdAt && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4 }}>
            <span style={{ color: "#ccc" }}><IconCalendar /></span>
            <span style={{ fontSize: 12, color: "#aaa" }}>
              {t("Cadastrado em {data}", { data: dayjs(ue.createdAt).format("DD/MM/YYYY") })}
            </span>
          </div>
        )}

        {!ue.isModered && (
          <p style={{
            margin: "8px 0 0", fontSize: 12, color: "#b45309",
            background: "#fff7e6", borderRadius: 6, padding: "4px 10px",
            display: "inline-block",
          }}>
            {t("Seu cadastro está em análise. Prazo: até 48 horas.")}
          </p>
        )}
      </div>

      {/* Action */}
      <button
        onClick={() => onDetails(ue)}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "10px 16px", background: "#00b6bc", color: "#fff",
          border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13,
          cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
        }}
      >
        {t("Ver detalhes")} <IconArrow />
      </button>
    </div>
  );
};

// ─── Detail Modal ─────────────────────────────────────────────────────────────

const DetailModal: React.FC<{
  ue: IUserEstabilishment | null;
  onClose: () => void;
  onSaved: (id: string, instagram: string | null, site: string | null) => void;
}> = ({ ue, onClose, onSaved }) => {
  const t = useT();
  const [editing,   setEditing]   = useState(false);
  const [instagram, setInstagram] = useState("");
  const [site,      setSite]      = useState("");
  const [saving,    setSaving]    = useState(false);

  // repopula os campos sempre que o modal abre em outro estabelecimento
  useEffect(() => {
    if (!ue) return;
    setInstagram(ue.instagram ?? "");
    setSite(ue.site ?? "");
    setEditing(false);
  }, [ue]);

  if (!ue) return null;
  const estab = ue.estabilishment;

  const handleSave = () => {
    setSaving(true);
    const payload = {
      instagram: instagram.trim() || null,
      site:      site.trim()      || null,
    };
    updateUserEstabilishmentProfile(ue.id, payload)
      .then(() => {
        toast.success(t("Dados atualizados com sucesso."));
        onSaved(ue.id, payload.instagram, payload.site);
        setEditing(false);
      })
      .catch(() => toast.error(t("Não foi possível salvar. Tente novamente.")))
      .finally(() => setSaving(false));
  };

  const inp: React.CSSProperties = {
    width: "100%", padding: "8px 11px", border: "1.5px solid #e8e8e8",
    borderRadius: 8, fontSize: 13, color: "#333", outline: "none",
    background: "#fff", boxSizing: "border-box",
  };

  const row = (label: string, value: React.ReactNode) => (
    <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
      <span style={{ fontSize: 13, color: "#999", minWidth: 100 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#333", fontWeight: 500, flex: 1 }}>{value ?? "—"}</span>
    </div>
  );

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1300, padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff", borderRadius: 18, width: "100%", maxWidth: 480,
          padding: "28px 28px 24px", boxShadow: "0 8px 40px rgba(0,0,0,0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 12, overflow: "hidden",
            background: "#f5f5f5", display: "flex", alignItems: "center",
            justifyContent: "center", border: "1px solid #efefef", flexShrink: 0,
          }}>
            <AuthImage
              photoId={estab?.image}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              fallback={<span style={{ color: "#ccc" }}><IconStore /></span>}
            />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#111" }}>
              {estab?.name ?? "—"}
            </h3>
            <StatusBadge approved={!!ue.isModered} />
          </div>
        </div>

        {/* Details */}
        <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16 }}>
          {row(t("Endereço"), estab?.addressInGoogleFormat)}
          {row(t("Cadastrado em"), ue.createdAt ? dayjs(ue.createdAt).format(t("DD/MM/YYYY [às] HH:mm")) : null)}
          {row(t("Status"), <StatusBadge approved={!!ue.isModered} />)}
        </div>

        {/* ── Divulgação: Instagram | Site ──────────────────────────────── */}
        <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16, marginTop: 4 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#333" }}>{t("Divulgação")}</span>
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "5px 12px", background: "#e6f9fa", border: "none",
                  borderRadius: 8, color: "#00b6bc", fontWeight: 600,
                  fontSize: 12, cursor: "pointer",
                }}
              >
                <IconEdit /> {t("Editar")}
              </button>
            )}
          </div>

          {editing ? (
            <>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 4 }}>
                  {t("Instagram")}
                </label>
                <div style={{ display: "flex", alignItems: "center", border: "1.5px solid #e8e8e8", borderRadius: 8, background: "#fff" }}>
                  <span style={{ padding: "0 0 0 11px", color: "#aaa", fontSize: 13 }}>@</span>
                  <input
                    value={instagram}
                    onChange={(e) => setInstagram(e.target.value.replace(/^@/, ""))}
                    placeholder={t("seu_perfil")}
                    maxLength={100}
                    style={{ ...inp, border: "none", paddingLeft: 3 }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 4 }}>
                  {t("Site")}
                </label>
                <input
                  type="url"
                  value={site}
                  onChange={(e) => setSite(e.target.value)}
                  placeholder={t("www.exemplo.com.br")}
                  maxLength={255}
                  style={inp}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button
                  onClick={() => {
                    setInstagram(ue.instagram ?? "");
                    setSite(ue.site ?? "");
                    setEditing(false);
                  }}
                  disabled={saving}
                  style={{
                    padding: "8px 18px", borderRadius: 8, border: "1.5px solid #ddd",
                    background: "#fff", cursor: "pointer", fontWeight: 600,
                    fontSize: 13, color: "#555",
                  }}
                >
                  {t("Cancelar")}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "8px 20px", borderRadius: 8, border: "none",
                    background: "#00b6bc", color: "#fff", fontWeight: 700,
                    fontSize: 13, cursor: saving ? "not-allowed" : "pointer",
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving && <CircularProgress size={13} color="inherit" />} {t("Salvar")}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ color: "#aaa", flexShrink: 0 }}><IconInsta /></span>
                <span style={{ fontSize: 13, color: ue.instagram ? "#333" : "#bbb", fontWeight: 500 }}>
                  {ue.instagram ? `@${ue.instagram}` : t("Não informado")}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: "#aaa", flexShrink: 0 }}><IconGlobe /></span>
                <span style={{ fontSize: 13, color: ue.site ? "#333" : "#bbb", fontWeight: 500, wordBreak: "break-all" }}>
                  {ue.site || t("Não informado")}
                </span>
              </div>
            </>
          )}
        </div>

        {!ue.isModered && (
          <div style={{
            marginTop: 16, padding: "12px 14px", background: "#fff7e6",
            borderRadius: 10, fontSize: 13, color: "#92400e", lineHeight: 1.5,
          }}>
            <strong>{t("Cadastro em análise.")}</strong>{" "}
            {t("Nossa equipe irá avaliar os documentos enviados. O prazo é de até 48 horas. Após a aprovação, sua assinatura estará disponível.")}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{
              padding: "10px 24px", borderRadius: 10,
              border: "1.5px solid #ddd", background: "#fff",
              cursor: "pointer", fontWeight: 600, fontSize: 14, color: "#555",
            }}
          >
            {t("Fechar")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const MyEstablishments = () => {
  const t = useT();
  const navigate = useNavigate();
  const [estabs,   setEstabs]   = useState<IUserEstabilishment[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState<IUserEstabilishment | null>(null);

  // Status da análise manual dos documentos (docs + selfie). Enquanto está
  // "pending", avisamos aqui — é a tela onde o usuário volta para conferir
  // a situação do cadastro.
  const [docsStatus, setDocsStatus] = useState<string>("");

  // ── Adicionar novo estabelecimento à conta ────────────────────────────────
  const [addOpen,       setAddOpen]       = useState(false);
  const [addQuery,      setAddQuery]      = useState("");
  const [addResults,    setAddResults]    = useState<any[]>([]);
  const [addSearching,  setAddSearching]  = useState(false);
  const [addSaving,     setAddSaving]     = useState(false);
  const [addTaken,      setAddTaken]      = useState<Set<string>>(new Set());
  const [addError,      setAddError]      = useState("");

  // Etapa do modal: escolher o estabelecimento, depois enviar os documentos.
  // Mesmo processo do cadastro — cada estabelecimento novo precisa comprovar
  // o CNPJ, mesmo que o responsável já tenha sido verificado antes.
  const [addStep,     setAddStep]     = useState<1 | 2>(1);
  const [addChosen,   setAddChosen]   = useState<any>(null);
  const [addUeId,     setAddUeId]     = useState("");
  const [addFiles,    setAddFiles]    = useState<File[]>([]);
  const [addProgress, setAddProgress] = useState("");

  // busca com debounce, marcando os que já têm dono
  useEffect(() => {
    if (!addOpen) return;

    const termo = addQuery.trim();
    if (termo.length < 2) {
      setAddResults([]);
      return;
    }

    setAddSearching(true);
    const t = setTimeout(() => {
      searchEstabilishments(termo)
        .then(async (r: any) => {
          const encontrados = r?.data?.data ?? [];
          setAddResults(encontrados);

          // desabilita na lista o que já pertence a alguém
          if (encontrados.length > 0) {
            try {
              const chk: any = await checkEstabilishmentsTaken(
                encontrados.map((x: any) => x.id),
              );
              const taken: string[] = chk?.data?.data?.taken ?? [];
              setAddTaken(new Set(taken));
            } catch (_) {
              /* sem a checagem, o backend ainda valida ao adicionar */
            }
          }
        })
        .catch(() => setAddResults([]))
        .finally(() => setAddSearching(false));
    }, 400);

    return () => clearTimeout(t);
  }, [addQuery, addOpen]);

  /** Etapa 1 → cria o vínculo e avança para o envio dos documentos. */
  const handleAdd = (estab: any) => {
    if (addTaken.has(estab.id)) return;

    setAddSaving(true);
    setAddError("");

    addEstabilishmentToAccount(estab.id)
      .then((res: any) => {
        setAddUeId(res?.data?.data?.id ?? "");
        setAddChosen(estab);
        setAddStep(2);
      })
      .catch((err) => {
        setAddError(
          getApiErrorMessage(err, t("Não foi possível adicionar o estabelecimento.")),
        );
      })
      .finally(() => setAddSaving(false));
  };

  /** Etapa 2 → envia os documentos do estabelecimento recém-vinculado. */
  const handleAddDocuments = async () => {
    if (addFiles.length === 0) {
      setAddError(t("Envie ao menos um documento."));
      return;
    }
    if (!addUeId) {
      setAddError(t("Vínculo não identificado. Feche e tente novamente."));
      return;
    }

    setAddSaving(true);
    setAddError("");

    try {
      let enviados = 0;
      for (const file of addFiles) {
        enviados++;
        setAddProgress(
          t("Enviando documentos... ({atual}/{total})", {
            atual: enviados,
            total: addFiles.length,
          }),
        );
        await postUserEstabilishmentAttachment(addUeId, file, "document");
      }

      toast.success(
        t('"{nome}" foi adicionado. Os documentos entraram em análise.', {
          nome: addChosen?.name,
        }),
      );

      closeAddModal();

      // recarrega a lista com o novo estabelecimento
      const res = await getMyEstabilishments();
      const { data } = res as IRestGetMyEstabilishments;
      setEstabs(data.data);
    } catch (err) {
      setAddError(getApiErrorMessage(err, t("Falha ao enviar os documentos.")));
    } finally {
      setAddSaving(false);
      setAddProgress("");
    }
  };

  /** Limpa tudo ao fechar, para o modal não reabrir no meio do fluxo. */
  const closeAddModal = () => {
    setAddOpen(false);
    setAddStep(1);
    setAddQuery("");
    setAddResults([]);
    setAddChosen(null);
    setAddUeId("");
    setAddFiles([]);
    setAddError("");
    setAddProgress("");
  };
  const [planType,   setPlanType]   = useState<string>("");

  useEffect(() => {
    getMyEstabilishments()
      .then((res) => {
        const { data } = res as IRestGetMyEstabilishments;
        setEstabs(data.data);
      })
      .catch(() => toast.error(t("Erro ao carregar estabelecimentos.")))
      .finally(() => setLoading(false));

    // status da análise — falha aqui não bloqueia a tela
    getEstablishmentDashboard()
      .then((res) => {
        const d = (res as IRestDashboard).data.data;
        setDocsStatus(d.documentsStatus ?? "");
        setPlanType(d.planType ?? "");
      })
      .catch(() => {});
  // `t` fica fora das deps de propósito: incluí-lo refaria a consulta a
  // cada troca de idioma, perdendo página, filtros e rolagem.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", paddingTop: 60 }}>
        <CircularProgress style={{ color: "#00b6bc" }} />
      </div>
    );
  }

  return (
    <div style={{ padding: 16, maxWidth: 860 }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: "#111", margin: 0 }}>
          {t("Meus Estabelecimentos")}
        </h2>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => setAddOpen(true)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "10px 18px", background: "#fff", color: "#00b6bc",
              border: "1.5px solid #00b6bc", borderRadius: 10,
              fontWeight: 600, fontSize: 14, cursor: "pointer",
            }}
          >
            + {t("Adicionar estabelecimento")}
          </button>
          <button
            onClick={() => navigate("/establishment/subscription")}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "10px 18px", background: "#00b6bc", color: "#fff",
              border: "none", borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: "pointer",
            }}
          >
            {t("Ver assinaturas")}
          </button>
        </div>
      </div>

      {/* ── Análise dos documentos ──────────────────────────────────────── */}
      {(docsStatus === "pending" || docsStatus === "rejected") && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            background: docsStatus === "rejected" ? "#fff7e6" : "#e6f9fa",
            border: `1px solid ${docsStatus === "rejected" ? "#fde68a" : "#cdf3f5"}`,
            borderRadius: 12,
            padding: "14px 16px",
            marginBottom: 24,
          }}
        >
          <span style={{ fontSize: 20, lineHeight: 1 }}>
            {docsStatus === "rejected" ? "⚠️" : "⏳"}
          </span>
          <div>
            <p
              style={{
                margin: "0 0 4px",
                fontSize: 14,
                fontWeight: 700,
                color: "#111",
              }}
            >
              {docsStatus === "rejected"
                ? t("Precisamos de um ajuste nos seus documentos")
                : t("Seus documentos estão em análise")}
            </p>
            <p
              style={{
                margin: 0,
                fontSize: 13,
                color: "#666",
                lineHeight: 1.6,
              }}
            >
              {docsStatus === "rejected" ? (
                t("Encontramos uma divergência. Verifique seu e-mail — nossa equipe explicou o que precisa ser reenviado.")
              ) : (
                <>
                  {t("O prazo é de até")}{" "}
                  <strong>{t("72 horas")}</strong>
                  {t(". Seus estabelecimentos já estão cadastrados e o painel segue liberado normalmente.")}
                </>
              )}
              {planType === "connection" &&
                " " + t("As áreas de Banners e Notificações Push são liberadas após a aprovação.")}
            </p>
          </div>
        </div>
      )}

      {/* Status summary */}
      {estabs.length > 0 && (
        <div style={{
          display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap",
        }}>
          <div style={{
            background: "#fff", border: "1.5px solid #efefef", borderRadius: 10,
            padding: "10px 18px", display: "flex", gap: 8, alignItems: "center",
          }}>
            <span style={{ fontSize: 22, fontWeight: 700, color: "#00b6bc" }}>{estabs.length}</span>
            <span style={{ fontSize: 13, color: "#888" }}>{estabs.length !== 1 ? t("Estabelecimentos") : t("Estabelecimento")}</span>
          </div>
          <div style={{
            background: "#fff", border: "1.5px solid #efefef", borderRadius: 10,
            padding: "10px 18px", display: "flex", gap: 8, alignItems: "center",
          }}>
            <span style={{ fontSize: 22, fontWeight: 700, color: "#1a7a4a" }}>
              {estabs.filter((e) => e.isModered).length}
            </span>
            <span style={{ fontSize: 13, color: "#888" }}>{estabs.filter((e) => e.isModered).length !== 1 ? t("Aprovados") : t("Aprovado")}</span>
          </div>
          <div style={{
            background: "#fff", border: "1.5px solid #efefef", borderRadius: 10,
            padding: "10px 18px", display: "flex", gap: 8, alignItems: "center",
          }}>
            <span style={{ fontSize: 22, fontWeight: 700, color: "#b45309" }}>
              {estabs.filter((e) => !e.isModered).length}
            </span>
            <span style={{ fontSize: 13, color: "#888" }}>{estabs.filter((e) => !e.isModered).length !== 1 ? t("Pendentes") : t("Pendente")}</span>
          </div>
        </div>
      )}

      {/* List */}
      {estabs.length === 0 ? (
        <div style={{
          background: "#fff", border: "1.5px solid #efefef", borderRadius: 14,
          padding: "60px 24px", textAlign: "center",
        }}>
          <div style={{ marginBottom: 12 }}><IconEmpty /></div>
          <p style={{ fontSize: 16, fontWeight: 600, color: "#555", margin: "0 0 6px" }}>
            {t("Nenhum estabelecimento cadastrado")}
          </p>
          <p style={{ fontSize: 13, color: "#aaa", margin: 0 }}>
            {t("Entre em contato com a equipe Pertalks para cadastrar seu estabelecimento.")}
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {estabs.map((ue) => (
            <EstablishmentCard
              key={ue.id}
              ue={ue}
              onDetails={setSelected}
            />
          ))}
        </div>
      )}

      {/* Detail modal */}

      {/* ── Modal: adicionar estabelecimento ─────────────────────────────── */}
      {addOpen && (
        <div
          onClick={() => !addSaving && closeAddModal()}
          style={{
            position: "fixed", inset: 0, zIndex: 60,
            background: "rgba(0,0,0,.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 16, width: "100%", maxWidth: 520,
              maxHeight: "80vh", display: "flex", flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: "22px 24px 0" }}>
              <h3 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 700, color: "#111" }}>
                {addStep === 1 ? t("Adicionar estabelecimento") : t("Documentos do estabelecimento")}
              </h3>
              <p style={{ margin: "0 0 16px", fontSize: 13, color: "#888", lineHeight: 1.6 }}>
                {addStep === 1
                  ? t("Busque pelo nome e selecione o estabelecimento que você gerencia.")
                  : t('Envie o Cartão CNPJ de "{nome}" para análise.', { nome: addChosen?.name })}
              </p>

              {addStep === 1 && (
              <input
                autoFocus
                value={addQuery}
                onChange={(e) => setAddQuery(e.target.value)}
                placeholder={t("Digite o nome do estabelecimento")}
                style={{
                  width: "100%", padding: "11px 14px", fontSize: 14,
                  border: "1.5px solid #e8e8e8", borderRadius: 10,
                  outline: "none", boxSizing: "border-box",
                }}
              />

              )}

              {addStep === 1 && addQuery.trim().length > 0 && addQuery.trim().length < 2 && (
                <p style={{ margin: "8px 0 0", fontSize: 12, color: "#aaa" }}>
                  {t("Digite ao menos 2 caracteres para buscar.")}
                </p>
              )}

              {addError && (
                <p style={{
                  margin: "12px 0 0", fontSize: 13, color: "#92400e",
                  background: "#fff7e6", border: "1px solid #fde68a",
                  borderRadius: 8, padding: "10px 12px", lineHeight: 1.5,
                }}>
                  {addError}
                </p>
              )}
            </div>

            {/* Etapa 2 — envio dos documentos */}
            {addStep === 2 && (
              <div style={{ flex: 1, overflowY: "auto", padding: "12px 24px" }}>

                {/* Documentos exigidos — mesma lista do cadastro */}
                <div style={{
                  background: "#fafafa", border: "1px solid #eee",
                  borderRadius: 10, padding: "14px 16px", marginBottom: 16,
                }}>
                  <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700, color: "#111" }}>
                    {t("Documento obrigatório")}
                  </p>
                  <p style={{ margin: 0, fontSize: 13, color: "#555", lineHeight: 1.7 }}>
                    <strong>{t("Cartão CNPJ:")}</strong>{" "}
                    {t("comprova a inscrição e a situação cadastral ativa do estabelecimento perante a Receita Federal.")}
                  </p>
                  <p style={{ margin: "10px 0 0", fontSize: 12, color: "#999", lineHeight: 1.6 }}>
                    {t("Seu documento pessoal já foi validado no cadastro — não precisa reenviar.")}
                  </p>
                </div>

                <label
                  htmlFor="add-docs-input"
                  style={{
                    display: "block", border: "2px dashed #d5d5d5", borderRadius: 12,
                    padding: "28px 20px", textAlign: "center", cursor: "pointer",
                    background: "#fafafa", marginBottom: 12,
                  }}
                >
                  <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
                  <p style={{ margin: "0 0 4px", fontWeight: 700, fontSize: 14, color: "#333" }}>
                    {t("Clique para enviar os documentos")}
                  </p>
                  <p style={{ margin: 0, fontSize: 12, color: "#999" }}>
                    {t("PDF, JPG ou PNG")}
                  </p>
                  <input
                    id="add-docs-input"
                    type="file"
                    multiple
                    accept="application/pdf,image/jpeg,image/png"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const novos = Array.from(e.target.files ?? []);
                      if (novos.length === 0) return;
                      setAddError("");
                      setAddFiles((prev) => [...prev, ...novos]);
                    }}
                  />
                </label>

                {addFiles.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      gap: 10, padding: "10px 12px", marginBottom: 8,
                      border: "1.5px solid #f0f0f0", borderRadius: 10, fontSize: 13,
                    }}
                  >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {f.name}
                    </span>
                    <button
                      onClick={() => setAddFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      disabled={addSaving}
                      style={{
                        background: "none", border: "none", color: "#ef4444",
                        cursor: "pointer", fontSize: 13, fontWeight: 600, flexShrink: 0,
                      }}
                    >
                      {t("Remover")}
                    </button>
                  </div>
                ))}

                {addProgress && (
                  <p style={{
                    display: "flex", alignItems: "center", gap: 6,
                    fontSize: 13, color: "#00b6bc", margin: "12px 0 0",
                  }}>
                    <CircularProgress size={12} style={{ color: "#00b6bc" }} /> {addProgress}
                  </p>
                )}
              </div>
            )}

            {/* Etapa 1 — resultados da busca */}
            {addStep === 1 && (
            <div style={{ flex: 1, overflowY: "auto", padding: "12px 24px" }}>
              {addSearching && (
                <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
                  <CircularProgress size={22} style={{ color: "#00b6bc" }} />
                </div>
              )}

              {!addSearching && addQuery.trim().length >= 2 && addResults.length === 0 && (
                <p style={{ fontSize: 13, color: "#aaa", textAlign: "center", padding: 16 }}>
                  {t('Nenhum estabelecimento encontrado para "{termo}".', { termo: addQuery })}
                </p>
              )}

              {!addSearching && addResults.map((r) => {
                const taken = addTaken.has(r.id);
                return (
                  <div
                    key={r.id}
                    onClick={() => !addSaving && handleAdd(r)}
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 10,
                      padding: "12px 14px", marginBottom: 8,
                      border: "1.5px solid #f0f0f0", borderRadius: 10,
                      cursor: taken || addSaving ? "not-allowed" : "pointer",
                      opacity: taken ? 0.55 : 1,
                      background: "#fff",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: taken ? "#888" : "#111" }}>
                          {r.name}
                        </span>
                        {taken && (
                          <span style={{
                            fontSize: 10, fontWeight: 700, color: "#b45309",
                            background: "#fff7e6", borderRadius: 999, padding: "2px 8px",
                          }}>
                            {t("Já cadastrado")}
                          </span>
                        )}
                      </div>
                      {r.addressInGoogleFormat && (
                        <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
                          {r.addressInGoogleFormat}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            )}

            <div style={{
              padding: "14px 24px 20px", borderTop: "1px solid #f0f0f0",
              display: "flex", justifyContent: "flex-end", gap: 10,
            }}>
              <button
                onClick={closeAddModal}
                disabled={addSaving}
                style={{
                  padding: "10px 22px", background: "#fff", color: "#888",
                  border: "1.5px solid #e0e0e0", borderRadius: 10,
                  fontWeight: 600, fontSize: 14, cursor: "pointer",
                }}
              >
                {addStep === 2 ? t("Cancelar") : t("Fechar")}
              </button>

              {addStep === 2 && (
                <button
                  onClick={handleAddDocuments}
                  disabled={addSaving || addFiles.length === 0}
                  style={{
                    padding: "10px 24px", background: "#00b6bc", color: "#fff",
                    border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14,
                    cursor: addSaving || addFiles.length === 0 ? "not-allowed" : "pointer",
                    opacity: addSaving || addFiles.length === 0 ? 0.6 : 1,
                  }}
                >
                  {addSaving ? t("Enviando...") : t("Enviar documentos")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <DetailModal
        ue={selected}
        onClose={() => setSelected(null)}
        onSaved={(id, instagram, site) => {
          // instagram/site vivem no Profile do usuário — refletem em todos os vínculos
          setEstabs((prev) => prev.map((e) => ({ ...e, instagram, site })));
          setSelected((prev) => (prev && prev.id === id ? { ...prev, instagram, site } : prev));
        }}
      />
    </div>
  );
};